import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { Textarea } from '../../components/ui/FormField.js';
import { Modal } from '../../components/ui/Modal.js';
import {
  type BulkCodeResponse,
  type DiscountCode,
  discountsApi,
} from '../../services/discounts.js';
import { CodeBulkResultDialog } from './CodeBulkResultDialog.js';
import { mytTime } from './CodeShareCells.js';

const REASON_MAX = 300;

interface CancelReasonDialogProps {
  open: boolean;
  title: string;
  /** What happens, said before anything does. */
  children?: ReactNode;
  label: string;
  confirmLabel: string;
  confirmVariant: 'primary' | 'danger';
  pending: boolean;
  error: unknown;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}

/** 411 (#1513, mock-up frames 2 and 4): one reason for a request or a rejection; the platform's refusal in its words. */
export function CancelReasonDialog({
  open,
  title,
  children,
  label,
  confirmLabel,
  confirmVariant,
  pending,
  error,
  onClose,
  onConfirm,
}: CancelReasonDialogProps) {
  const [reason, setReason] = useState('');
  const close = () => {
    setReason('');
    onClose();
  };
  return (
    <Modal open={open} onClose={close} title={title}>
      <div className="space-y-4 px-6 py-4 text-sm text-ink">
        {children}
        <div className="flex flex-col gap-1">
          <label htmlFor="code-cancel-reason" className="font-medium">
            {label}
          </label>
          <Textarea
            id="code-cancel-reason"
            rows={3}
            maxLength={REASON_MAX}
            value={reason}
            onInput={(e) => setReason((e.target as HTMLTextAreaElement).value)}
          />
          <p className="text-right text-xs text-steel">
            {reason.length} / {REASON_MAX}
          </p>
        </div>
        {error != null && (
          <p role="alert" className="text-red-600">
            {(error as { message?: string }).message || 'Something went wrong.'}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="secondary" onClick={close}>
            Back
          </Button>
          <Button
            size="sm"
            variant={confirmVariant}
            disabled={reason.trim().length === 0}
            loading={pending}
            onClick={() => onConfirm(reason.trim())}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/** 411: "Cancel 2 codes?" — the request's consequences, as the approved mock-up states them. */
export function askTitle(count: number) {
  return `Cancel ${count} code${count === 1 ? '' : 's'}?`;
}
export function rejectTitle(count: number) {
  return `Reject the cancellation of ${count} code${count === 1 ? '' : 's'}?`;
}
export function AskConsequences() {
  return (
    <ul className="list-disc space-y-1 pl-5">
      <li>They stop working at once, for everyone.</li>
      <li>
        A SuperAdmin must approve before they are cancelled for good. If it is rejected, they work
        again.
      </li>
      <li>A customer a code was shared with is not told.</li>
      <li>Used, expired or already cancelled codes are skipped.</li>
    </ul>
  );
}
export function RejectConsequences({ count }: { count: number }) {
  return <p>{count === 1 ? 'The code works again' : 'The codes work again'}, exactly as before.</p>;
}

/**
 * 411 (FR-007/FR-008, mock-up frames 1, 3, 4): where a code's cancellation stands. Pending: who asked, when, why, and —
 * by the platform's answer — Approve / Reject… for a SuperAdmin who did not ask, one line for the asker, "Waiting for a
 * SuperAdmin" for everyone else. Approved: who asked and who approved. Rejected: who rejected and why.
 */
export function CancellationCell({ code, discountId }: { code: DiscountCode; discountId: string }) {
  const queryClient = useQueryClient();
  const [rejecting, setRejecting] = useState(false);
  const [result, setResult] = useState<BulkCodeResponse | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['discount-codes', discountId] });
  const approve = useMutation({
    mutationFn: () => discountsApi.approveCancelCodes(discountId, [code.id]),
    onSuccess: (data) => setResult(data),
  });
  const reject = useMutation({
    mutationFn: (reason: string) => discountsApi.rejectCancelCodes(discountId, [code.id], reason),
    onSuccess: (data) => {
      setRejecting(false);
      setResult(data);
    },
  });

  const k = code.cancellation;
  if (!k) {
    return (
      <span data-testid="code-cancellation" className="text-steel">
        —
      </span>
    );
  }
  const when = (iso: string | null) => (iso ? ` · ${mytTime(iso)}` : '');
  const asker = k.asked_by?.label || 'staff';
  const decider = k.decided_by?.label || 'a SuperAdmin';

  return (
    <div data-testid="code-cancellation" className="flex min-w-[12rem] flex-col gap-1">
      {k.status === 'pending' && (
        <>
          <span>
            <Badge variant="warning">Cancellation pending</Badge>
          </span>
          <span className="text-xs text-steel">{`asked by ${asker}${when(k.asked_at)}: ${k.reason}`}</span>
          {code.cancellation_decision_for === 'allowed' && (
            <div className="flex gap-2">
              <Button size="sm" loading={approve.isPending} onClick={() => approve.mutate()}>
                Approve
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setRejecting(true)}>
                Reject…
              </Button>
            </div>
          )}
          {code.cancellation_decision_for === 'own' && (
            <span className="text-xs text-steel">
              You asked for this, so another SuperAdmin must decide.
            </span>
          )}
          {code.cancellation_decision_for === 'no_permission' && (
            <span className="text-xs text-steel">Waiting for a SuperAdmin.</span>
          )}
        </>
      )}
      {k.status === 'approved' && (
        <span className="text-xs text-steel">
          {`asked by ${asker}, approved by ${decider}${when(k.decided_at)}: ${k.reason}`}
        </span>
      )}
      {k.status === 'rejected' && (
        <>
          <span>
            <Badge>Cancellation rejected</Badge>
          </span>
          <span className="text-xs text-steel">
            {`by ${decider}${when(k.decided_at)}: ${k.reject_reason ?? ''}`}
          </span>
        </>
      )}
      {approve.isError && (
        <span role="alert" className="text-xs text-red-600">
          {(approve.error as { message?: string }).message || 'Could not approve.'}
        </span>
      )}
      <CancelReasonDialog
        open={rejecting}
        title={rejectTitle(1)}
        label="Why reject?"
        confirmLabel="Reject"
        confirmVariant="primary"
        pending={reject.isPending}
        error={reject.isError ? reject.error : null}
        onClose={() => setRejecting(false)}
        onConfirm={(reason) => reject.mutate(reason)}
      >
        <RejectConsequences count={1} />
      </CancelReasonDialog>
      <CodeBulkResultDialog
        result={result}
        labels={{ [code.id]: code.code }}
        onClose={() => {
          setResult(null);
          refresh();
        }}
      />
    </div>
  );
}
